import type { CompileDiagnostic, OmniStudioEdge, OmniStudioGraph, OmniStudioNode } from "@/lib/omnistudio/compiler/directorCompiler.types";
import type { OmniStudioHandoffPayload } from "@/lib/guidedStudio/buildOmniStudioHandoffPayload";
import type { OmniStudioSourceItem, OmniStudioSourceStageState, OmniStudioSourceType } from "@/components/OmniStudio/omnistudioSources.model";
import type { GuidedStudioAuthoredPacket } from "@/features/guidedStudio/rc1/guidedStudio.types";

export type GuidedStudioOmniStudioImport = {
  graph: OmniStudioGraph;
  selectedNodeId: string | null;
  selectedSourceId: string | null;
  sources: OmniStudioSourceItem[];
  authoredDiagnostics: CompileDiagnostic[];
  packet: GuidedStudioAuthoredPacket;
  summary: {
    title: string;
    sceneCount: number;
    npcCount: number;
    sourceCount: number;
    noteCount: number;
    continuityWarningCount: number;
    scapeModuleCount: number;
    projectGoal: string;
    creativeLens: string;
    emotionalDirection: string[];
  };
};

function unique(values: string[]): string[] {
  return [...new Set(values.map((value) => value.trim()).filter(Boolean))];
}

function normalizeLabel(value: string): string {
  return value.replaceAll("_", " ");
}

function assetTypeToSourceType(kind: OmniStudioHandoffPayload["assets"]["byId"][string]["kind"]): OmniStudioSourceType {
  if (kind === "video") return "video";
  if (kind === "audio") return "audio";
  if (kind === "image") return "still";
  if (kind === "text") return "script";
  return "reference";
}

function assetStageState(kind: OmniStudioHandoffPayload["assets"]["byId"][string]["kind"]): OmniStudioSourceStageState {
  return kind === "reference" ? "reference" : "bound";
}

function buildAssetSources(payload: OmniStudioHandoffPayload): OmniStudioSourceItem[] {
  const importedAt = Date.parse(payload.createdAt);

  return payload.assets.allIds.flatMap((assetId, index) => {
      const asset = payload.assets.byId[assetId];
      if (!asset) return [];

      const boundSceneIds = payload.scenes
        .filter((scene) => scene.assetIds.includes(assetId))
        .map((scene) => `guided-scene:${scene.id}`);

      return [{
        id: `guided-source:asset:${asset.id}`,
        title: asset.title,
        type: assetTypeToSourceType(asset.kind),
        stageState: boundSceneIds.length > 0 ? assetStageState(asset.kind) : asset.kind === "reference" ? "reference" : "staged",
        boundSceneIds,
        boundArcIds: [] as string[],
        tags: unique([...(asset.tags ?? []), asset.kind, "guided-studio"]),
        note: asset.notes,
        sourceUrl: undefined,
        queuedForCompile: boundSceneIds.length > 0 && asset.kind !== "reference",
        importedAt: importedAt + index,
      } satisfies OmniStudioSourceItem];
    });
}

function buildNoteSources(payload: OmniStudioHandoffPayload, offset: number): OmniStudioSourceItem[] {
  const importedAt = Date.parse(payload.createdAt) + offset;

  return payload.notes.map((note, index) => {
    const boundSceneIds = payload.scenes
      .filter((scene) => scene.noteIds.includes(note.id))
      .map((scene) => `guided-scene:${scene.id}`);

    return {
      id: `guided-source:note:${note.id}`,
      title: note.title,
      type: "script",
      stageState: boundSceneIds.length > 0 ? "bound" : "staged",
      boundSceneIds,
      boundArcIds: [] as string[],
      tags: ["guided-note", "guided-studio"],
      note: note.body,
      sourceUrl: undefined,
      queuedForCompile: boundSceneIds.length > 0,
      importedAt: importedAt + index,
    } satisfies OmniStudioSourceItem;
  });
}

function buildAuthoredDiagnostics(payload: OmniStudioHandoffPayload): CompileDiagnostic[] {
  const diagnostics: CompileDiagnostic[] = [];

  if (payload.scenes.length === 0) {
    diagnostics.push({
      level: "error",
      code: "GUIDED_HANDOFF_NO_SCENES",
      message: "Guided Studio handed off without any authored scenes. OmniStudio can open, but scene review remains incomplete.",
    });
  }

  payload.continuityWarnings.forEach((warning, index) => {
    diagnostics.push({
      level: "warning",
      code: `GUIDED_CONTINUITY_${index + 1}`,
      message: warning,
      nodeIds: payload.scenes.map((scene) => `guided-scene:${scene.id}`),
    });
  });

  payload.scenes.forEach((scene) => {
    const sceneNodeId = `guided-scene:${scene.id}`;

    if (scene.assetIds.length === 0) {
      diagnostics.push({
        level: "warning",
        code: "GUIDED_SCENE_NO_SOURCES",
        message: `${scene.title} has no bound assets yet. The source rail is staged, but this scene still needs direct source support.`,
        nodeIds: [sceneNodeId],
      });
    }

    if (scene.npcIds.length === 0) {
      diagnostics.push({
        level: "info",
        code: "GUIDED_SCENE_NO_CAST",
        message: `${scene.title} has no assigned NPC participants yet.`,
        nodeIds: [sceneNodeId],
      });
    }
  });

  const unboundAssetTitles = payload.assets.allIds
    .filter((assetId) => payload.scenes.every((scene) => !scene.assetIds.includes(assetId)))
    .map((assetId) => payload.assets.byId[assetId]?.title)
    .filter((title): title is string => Boolean(title));

  if (unboundAssetTitles.length > 0) {
    diagnostics.push({
      level: "warning",
      code: "GUIDED_UNBOUND_SOURCES",
      message: `${unboundAssetTitles.length} guided source${unboundAssetTitles.length === 1 ? " is" : "s are"} unbound: ${unboundAssetTitles.slice(0, 3).join(", ")}${unboundAssetTitles.length > 3 ? "..." : ""}.`,
    });
  }

  return diagnostics;
}

export function buildOmniStudioGraphFromHandoff(payload: OmniStudioHandoffPayload): GuidedStudioOmniStudioImport {
  const arcId = `guided-arc:${payload.projectId}`;
  const nodes: OmniStudioNode[] = [
    {
      id: arcId,
      type: "arc",
      label: `${payload.title} Arc`,
      x: 88,
      y: 96,
    },
  ];
  const edges: OmniStudioEdge[] = [];

  payload.npcs.forEach((npc, index) => {
    nodes.push({
      id: `guided-character:${npc.id}`,
      type: "character",
      name: npc.name,
      traits: [npc.roleType, npc.archetype, npc.tone].filter(Boolean),
      x: 88,
      y: 220 + index * 124,
    });
  });

  payload.scenes.forEach((scene, index) => {
    const sceneNodeId = `guided-scene:${scene.id}`;
    nodes.push({
      id: sceneNodeId,
      type: "scene",
      title: scene.title,
      x: 360 + index * 264,
      y: 120 + (index % 2) * 188,
      durationHintSec: Math.max(8, 8 + scene.assetIds.length * 2 + scene.npcIds.length),
      participants: payload.npcs.filter((npc) => scene.npcIds.includes(npc.id)).map((npc) => npc.name),
      mood: scene.moodTags[0] ?? payload.intent.emotionalDirection[0] ?? "guided",
    });

    edges.push({ id: `guided-edge:arc:${scene.id}`, from: sceneNodeId, to: arcId, type: "belongs_to_arc" });

    if (index > 0) {
      edges.push({
        id: `guided-edge:order:${payload.scenes[index - 1].id}:${scene.id}`,
        from: `guided-scene:${payload.scenes[index - 1].id}`,
        to: sceneNodeId,
        type: "precedes",
      });
    }

    payload.npcs
      .filter((npc) => scene.npcIds.includes(npc.id))
      .forEach((npc) => {
        edges.push({
          id: `guided-edge:participant:${npc.id}:${scene.id}`,
          from: `guided-character:${npc.id}`,
          to: sceneNodeId,
          type: "participates",
        });
      });
  });

  payload.notes.forEach((note, index) => {
    const relatedScene = payload.scenes.find((scene) => scene.noteIds.includes(note.id)) ?? payload.scenes[index % Math.max(payload.scenes.length, 1)];
    const noteId = `guided-note:${note.id}`;
    nodes.push({
      id: noteId,
      type: "note",
      text: `${note.title}: ${note.body}`,
      x: 360 + (index % Math.max(payload.scenes.length, 1)) * 264,
      y: 410 + Math.floor(index / Math.max(payload.scenes.length, 1)) * 96,
    });

    if (relatedScene) {
      edges.push({
        id: `guided-edge:note:${note.id}:${relatedScene.id}`,
        from: noteId,
        to: `guided-scene:${relatedScene.id}`,
        type: "relates",
      });
    }
  });

  const graph: OmniStudioGraph = {
    meta: {
      version: 1,
      projectId: payload.projectId,
    },
    nodes,
    edges,
  };

  const assetSources = buildAssetSources(payload);
  const noteSources = buildNoteSources(payload, assetSources.length + 1);
  const sources = [...assetSources, ...noteSources];
  const authoredDiagnostics = buildAuthoredDiagnostics(payload);

  return {
    graph,
    selectedNodeId: payload.scenes[0] ? `guided-scene:${payload.scenes[0].id}` : arcId,
    selectedSourceId: sources[0]?.id ?? null,
    sources,
    authoredDiagnostics,
    packet: payload.packet,
    summary: {
      title: payload.title,
      sceneCount: payload.scenes.length,
      npcCount: payload.npcs.length,
      sourceCount: sources.length,
      noteCount: payload.notes.length,
      continuityWarningCount: payload.continuityWarnings.length,
      scapeModuleCount: payload.scape?.modules.length ?? 0,
      projectGoal: normalizeLabel(payload.intent.projectGoal),
      creativeLens: normalizeLabel(payload.intent.creativeLens),
      emotionalDirection: payload.intent.emotionalDirection.map(normalizeLabel),
    },
  };
}